import React, {
  createContext,
  PropsWithChildren,
  useMemo,
  useState,
} from "react";

import { debounce } from "lodash";

import useListActiveDestinations from "./queries/useListActiveDestinations";
import useFetchLatestReport from "./queries/useFetchLatestReport";
import {
  sortByFirstDetectedFn,
  sortByLastDetectedFn,
  sortByNameFn,
  sortByPagesDetectedFn,
  sortByRiskFn,
  WebTrackerManagerColumn,
  WebTrackerManagerRow,
  WebTrackerManagerSort,
} from "./WebTrackerManagerPage/sort";
import {
  effectiveRiskLevel,
  LatestWebTrackerReportResponse,
  WebTrackerRiskLevel,
} from "./types";
import useWebTrackerOverviewColumns from "./useWebTrackerOverviewColumns";
import { LoadedDestinationInfo } from "../types/global";

export type OverviewDataContextType = {
  activeDestinations: LoadedDestinationInfo[];
  columns: WebTrackerManagerColumn[];
  latestReport: LatestWebTrackerReportResponse | undefined;
  latestReportLoading: boolean;
  latestReportError: boolean;
  onSearchTrackers: (searchString: string) => void;
  searchStringDisplay: string;
  riskFilter: WebTrackerRiskLevel[];
  setRiskFilter: React.Dispatch<React.SetStateAction<WebTrackerRiskLevel[]>>;
  trackerNameHovered: string | null;
  setTrackerNameHovered: React.Dispatch<React.SetStateAction<string | null>>;
  trackerReportRows: WebTrackerManagerRow[];
  sort: WebTrackerManagerSort;
  onSort: (clickedColumn: string) => void;
  setSort: React.Dispatch<React.SetStateAction<WebTrackerManagerSort>>;
  isLoadingActiveDestinations: boolean;
  potentialDuplicateTrackerNames: Set<string>;
};

export const OverviewDataContext = createContext<OverviewDataContextType>({
  columns: [],
  riskFilter: [],
  searchStringDisplay: "",
  setRiskFilter: () => {},
  setSort: () => {},
  sort: {
    column: "Risk",
    direction: "desc",
  },
  onSort: () => {},
  trackerNameHovered: null,
  activeDestinations: [],
  isLoadingActiveDestinations: true,
  latestReportLoading: true,
  latestReportError: false,
  latestReport: undefined,
  onSearchTrackers: () => {},
  potentialDuplicateTrackerNames: new Set<string>(),
  setTrackerNameHovered: () => {},
  trackerReportRows: [],
});

export type OverviewDataContextProviderProps = {};

function OverviewDataContextProvider({
  children,
}: PropsWithChildren<OverviewDataContextProviderProps>) {
  const columns = useWebTrackerOverviewColumns();
  const [riskFilter, setRiskFilter] = useState<WebTrackerRiskLevel[]>([]);
  const [searchStringDisplay, setSearchStringDisplay] = useState<string>("");
  const [searchString, setSearchString] = useState<string>("");
  const [sort, setSort] = useState<WebTrackerManagerSort>({
    column: "Risk",
    direction: "desc",
  });
  const [trackerNameHovered, setTrackerNameHovered] = useState<string | null>(
    null
  );

  const {
    data: activeDestinationsResponse,
    isLoading: isLoadingActiveDestinations,
  } = useListActiveDestinations(1000);

  const activeDestinations = useMemo(() => {
    return activeDestinationsResponse?.destinations ?? [];
  }, [activeDestinationsResponse]);

  const {
    isLoading: latestReportLoading,
    isError: latestReportError,
    data: latestReport,
  } = useFetchLatestReport();

  const onSearch = debounce((searchString: string) => {
    setSearchString(searchString);
  }, 500);

  const onSearchTrackers = (searchString: string) => {
    setSearchStringDisplay(searchString);
    onSearch(searchString);
  };

  const onSort = (clickedColumn: string) => {
    const column = columns.find((c) => c.name === clickedColumn);
    if (!column || !column.isSortable) {
      return;
    }

    if (sort.column === column.name) {
      setSort({
        column: column.name,
        direction: sort.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSort({ column: column.name, direction: "asc" });
    }
  };

  const reportRows = useMemo(() => {
    if (latestReport === undefined) {
      return [];
    }

    return latestReport.trackers.map((webTracker) => {
      const trackerStats = latestReport.reportTrackerStats.find(
        (stats) => stats.tracker === webTracker.name
      );

      return {
        onNumPages: trackerStats!.approxNumPages,
        firstDetectedAt: trackerStats!.firstDetectedAt,
        lastDetectedAt: trackerStats!.lastDetectedAt,
        webTracker,
      };
    });
  }, [latestReport]);

  const filteredReportRows = useMemo(() => {
    const searchStringLower = searchString.toLowerCase().trim();

    const rowsFilteredByRisk =
      riskFilter.length === 0
        ? reportRows
        : reportRows.filter((row) => {
            return riskFilter.includes(effectiveRiskLevel(row.webTracker));
          });

    if (searchStringLower === "") {
      return rowsFilteredByRisk;
    }

    return rowsFilteredByRisk.filter((row) => {
      return row.webTracker.name.toLowerCase().includes(searchStringLower);
    });
  }, [reportRows, riskFilter, searchString]);

  const trackerReportRows = useMemo(() => {
    switch (sort.column) {
      case "Tracking Tool":
        return filteredReportRows.sort(sortByNameFn(sort));
      case "Pages Detected":
        return filteredReportRows.sort(sortByPagesDetectedFn(sort));
      case "First Detected":
        return filteredReportRows.sort(sortByFirstDetectedFn(sort));
      case "Last Detected":
        return filteredReportRows.sort(sortByLastDetectedFn(sort));
      case "Risk":
      default:
        return filteredReportRows.sort(sortByRiskFn(sort));
    }
  }, [sort, filteredReportRows]);

  const potentialDuplicateTrackerNames: Set<string> = useMemo(() => {
    const activeDestinationsSet = new Set(
      activeDestinations.map((d) => d.name as string)
    );

    const duplicateTrackerNames = reportRows
      .filter((r) => activeDestinationsSet.has(r.webTracker.destinationKey))
      .map((r) => r.webTracker.destinationKey);

    return new Set(duplicateTrackerNames);
  }, [activeDestinations, reportRows]);

  return (
    <OverviewDataContext.Provider
      value={{
        activeDestinations,
        columns,
        isLoadingActiveDestinations,
        latestReport,
        latestReportError,
        latestReportLoading,
        onSearchTrackers,
        potentialDuplicateTrackerNames,
        riskFilter,
        searchStringDisplay,
        setRiskFilter,
        setSort,
        setTrackerNameHovered,
        sort,
        onSort,
        trackerNameHovered,
        trackerReportRows,
      }}
    >
      {children}
    </OverviewDataContext.Provider>
  );
}

export default OverviewDataContextProvider;
